import { Component, input, OnInit } from '@angular/core';
import { NgTemplateOutlet, AsyncPipe, SlicePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatNavList } from '@angular/material/list';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { Router, RouterLinkActive, RouterLink } from '@angular/router';

import { combineLatest, map, of } from 'rxjs';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';
import { MessageFacade } from 'src/app/modules/ngrx-store/message/message.facade';
import { RealtorFacade } from 'src/app/modules/ngrx-store/realtor/realtor.facade';
import { RootFacade } from 'src/app/modules/ngrx-store/facade';
import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';

import { AuthService } from 'src/app/modules/core/services/auth.service';

import { InteractionContext } from 'src/app/modules/shared/constants/adobe-analytics-fields';
import { InternalUrlPipe } from 'src/app/modules/shared/pipes/internal-url.pipe';
import { getPageLoadEvars, getCtaClickProps } from 'src/app/modules/shared/utilities/adobe-analytics.utils';
import { CORE_ROUTES, NAVIGATION_ROUTES, REALTOR_ROUTES } from 'src/app/modules/shared/constants/route-paths';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';
import { getPageNameFromUrl } from 'src/app/modules/shared/utilities/page-name.utils';
import { IfViewSizeDirective } from 'src/app/modules/shared/directives/if-view-size.directive';

const NAVIGATION_LABEL = {
	Dashboard: 'Dashboard',
	YourHome: 'Your Home',
	MessageCenter: 'Message Center',
	ConstructionProgress: 'Construction Progress',
	ServiceRequests: 'Service Requests',
	FinancialServices: 'Financial Services',
	Resources: 'Resources',
	ClientManagement: 'Client Management',
	ContactInformation: 'Contact Information'
}

type NavigationItem = {
	customClass: string;
	svg: string; // If an item has an SVG, it will not have a mat-icon
	icon: string; // vice-versa
	label: string;
	routerLink: string;
	exactMatch?: boolean;
}

@Component({
	selector: 'cp-navigation',
	templateUrl: './navigation.component.html',
	styleUrls: ['./navigation.component.scss'],
	imports: [IfViewSizeDirective, MatList, NgTemplateOutlet, MatListItem, RouterLinkActive, RouterLink, MatIcon, MatMenuTrigger, MatMenu, MatNavList, AsyncPipe, SlicePipe]
})

export class NavigationComponent extends UnsubscribeOnDestroy implements OnInit {
	isPreviewRealtor = input<boolean | undefined>();
	homeDetails$ = this.yourHomeFacade.homeDetails$;
	postCloseWithUpdates$ = this.rootFacade.postCloseWithUpdates$;
	showMessageCenter$ = this.rootFacade.showMessageCenter$;
	userInfo$ = this.appFacade.userInfo$;
	unreadMessageCount$ = this.messageFacade.getUnreadMessageCount();
	realtorInfo$ = this.yourHomeFacade.realtorInfo$;
	realtorGrantsSuccess$ = this.realtorFacade.realtorGrantsSuccess$;
	showClientManagement$ = this.realtorFacade.showClientManagement$;

	routes = NAVIGATION_ROUTES;

	isRealtor: boolean = false;
	// When the user is a realtor, all nav links must stay under the /realtor/* tree so the
	// B2C_REALTOR session is never re-evaluated by the login guard.
	private get routePrefix(): string {
		return this.isRealtor ? `${NAVIGATION_ROUTES.Realtor}/` : '';
	}

	navigationItems: NavigationItem[] = [
		{
			customClass: '',
			icon: 'dashboard',
			svg: '',
			label: NAVIGATION_LABEL.Dashboard,
			routerLink: NAVIGATION_ROUTES.Dashboard
		},
		{
			customClass: '',
			icon: '',
			svg: 'nav_your_home',
			label: NAVIGATION_LABEL.YourHome,
			routerLink: NAVIGATION_ROUTES.YourHome
		},
		{
			customClass: '',
			icon: 'construction',
			svg: '',
			label: NAVIGATION_LABEL.ServiceRequests,
			routerLink: NAVIGATION_ROUTES.ServiceRequests
		},
		{
			customClass: '',
			icon: '',
			svg: 'nav_financial_services',
			label: NAVIGATION_LABEL.FinancialServices,
			routerLink: NAVIGATION_ROUTES.FinancialServices
		},
		{
			customClass: '',
			icon: 'full_coverage',
			svg: '',
			label: NAVIGATION_LABEL.Resources,
			routerLink: NAVIGATION_ROUTES.Resources
		}
	];

	primaryOwnerName$ = this.authService.isRealtorUser()
		? combineLatest([
			this.realtorFacade.realtorGrants$,
			this.yourHomeFacade.salesAgreementId$
		]).pipe(
			map(([grants, salesAgreementId]) => 
				grants?.find(g => g.salesAgreementId === salesAgreementId)?.ownersName
			)
		)
		: of(undefined);

	constructor(
		private adobeAnalyticsService: AdobeAnalyticsService,
		private authService: AuthService,
		private router: Router,
		private appFacade: AppFacade,
		private rootFacade: RootFacade,
		private yourHomeFacade: YourHomeFacade,
		private messageFacade: MessageFacade,
		private realtorFacade: RealtorFacade,
		private internalUrlPipe: InternalUrlPipe) {
		super();
	}

	isRouteActive(route: string, exact = false): boolean {
		if (route === 'MORE') {
			return this.navigationItems.slice(0, 4).every(navigationItem => !this.currentUrl.includes(navigationItem.routerLink));
		}

		if (exact) {
			return this.currentUrl === route;
		}

		return this.currentUrl.includes(route);
	}

	get currentUrl(): string {
		return this.router.routerState.snapshot.url;
	}

	ngOnInit(): void {
		this.isRealtor = this.authService.isRealtorUser();

		// If provider is B2C_REALTOR, prefix all existing nav links with /realtor/ and remove Service Requests.
		if (this.isRealtor) {
			this.navigationItems = this.navigationItems
				.filter(item => item.label !== NAVIGATION_LABEL.ServiceRequests)
				.map(item => ({
					...item,
					routerLink: `${this.routePrefix}${item.routerLink}`
				}));
		}

		// Contact Information is hidden unless the API reports the feature as enabled for the home's market
		// (the global and market feature flag logic is evaluated by the API). Hidden while homeDetails loads.
		this.homeDetails$.pipe(
			this.takeUntilDestroyed()
		).subscribe(homeDetails => {
			const contactInformationIndex = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.ContactInformation);
			const showContactInformation = !!homeDetails?.contactInformationEnabled;

			if (showContactInformation && contactInformationIndex === -1) {
				this.navigationItems.push({
					customClass: '',
					icon: 'account_circle',
					svg: '',
					label: NAVIGATION_LABEL.ContactInformation,
					routerLink: `${this.routePrefix}${NAVIGATION_ROUTES.ContactDetails}`
				});
			} else if (!showContactInformation && contactInformationIndex >= 0) {
				this.navigationItems.splice(contactInformationIndex, 1);
			}
		});

		combineLatest([
			this.realtorGrantsSuccess$,
			this.showClientManagement$
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(([success, showClientManagement]) => {
			const clientManagementIndex = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.ClientManagement);

			// If grants have loaded, showClientManagement is true, and it's not in the navigation items array then add Client Management
			if (success) {
				if (this.isRealtor) {
					if (showClientManagement && clientManagementIndex === -1) {
						this.navigationItems.unshift({
							customClass: '',
							icon: 'add_home_work',
							svg: '',
							label: NAVIGATION_LABEL.ClientManagement,
							routerLink: `${NAVIGATION_ROUTES.Realtor}/${REALTOR_ROUTES.Homes}`
						});
					} else if (!showClientManagement && clientManagementIndex >= 0) {
						this.navigationItems.splice(clientManagementIndex, 1);
					}
				} else {
					// Handle Client Management — shown at the top of the nav in realtor preview mode so
					// the previewer can navigate back to the CM page to select a different agreement.
					if (this.isPreviewRealtor() && clientManagementIndex === -1 && showClientManagement) {
						this.navigationItems.unshift({
							customClass: '',
							icon: 'add_home_work',
							svg: '',
							label: NAVIGATION_LABEL.ClientManagement,
							routerLink: `/${CORE_ROUTES.Preview}/${NAVIGATION_ROUTES.Realtor}`,
							exactMatch: true
						});
					} else if ((!this.isPreviewRealtor() || !showClientManagement) && clientManagementIndex >= 0) {
						this.navigationItems.splice(clientManagementIndex, 1);
					}
				}
			}
		});

		// Hide Service Requests when previewing as a realtor; restore it when preview ends.
		// Skipped for realtor users — they never have Service Requests in their nav.
		if (!this.isRealtor) {
			// Handle Service Requests — hidden in realtor preview mode
			const serviceRequestsIndex = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.ServiceRequests);
			const isServiceRequestsPresent = serviceRequestsIndex >= 0;

			if (this.isPreviewRealtor() && isServiceRequestsPresent) {
				// Remove while in realtor preview mode
				this.navigationItems.splice(serviceRequestsIndex, 1);
			} else if (!this.isPreviewRealtor() && !isServiceRequestsPresent) {
				// Restore before Financial Services when preview ends
				const insertAt = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.FinancialServices);
				this.navigationItems.splice(insertAt >= 0 ? insertAt : this.navigationItems.length, 0, {
					customClass: '',
					icon: 'construction',
					svg: '',
					label: NAVIGATION_LABEL.ServiceRequests,
					routerLink: NAVIGATION_ROUTES.ServiceRequests
				});
			}
		}

		this.postCloseWithUpdates$.pipe(
			this.takeUntilDestroyed()
		).subscribe((showConstructionProgress) => {
			// Add Construction Progress if Updates Exists and not past closeOfEscrow
			const constructionProgressIndex = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.ConstructionProgress);

			if (showConstructionProgress && constructionProgressIndex === -1) {
				const index = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.YourHome);

				this.navigationItems.splice(index + 1, 0,
					{
						customClass: '',
						icon: 'foundation',
						svg: '',
						label: NAVIGATION_LABEL.ConstructionProgress,
						routerLink: `${this.routePrefix}${NAVIGATION_ROUTES.ConstructionProgress}`
					},
				);
			} else if (!showConstructionProgress && constructionProgressIndex >= 0) {
				this.navigationItems.splice(constructionProgressIndex, 1);
			}
		});

		combineLatest([
			this.messageFacade.messageThreadsSuccess$,
			this.showMessageCenter$
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(([messageThreadsSuccess, showMessageCenter]) => {
			if (messageThreadsSuccess && showMessageCenter && !this.navigationItems.some(i => i.label === NAVIGATION_LABEL.MessageCenter)) {
				let index = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.ConstructionProgress);

				// If Construction Progress is not in the navigation, add it after Your Home
				if (index === -1) {
					index = this.navigationItems.findIndex(i => i.label === NAVIGATION_LABEL.YourHome);
				}

				this.navigationItems.splice(index + 1, 0, {
					customClass: '',
					icon: '',
					svg: 'nav_message_center',
					label: NAVIGATION_LABEL.MessageCenter,
					routerLink: `${this.routePrefix}${NAVIGATION_ROUTES.MessageCenter}`
				});
			}
		});
	}

	changeRoute(route: string) {
		const transformedUrl = this.internalUrlPipe.transform(route);
		this.router.navigate([transformedUrl]);
	}

	trackNavigation(ctaTitle: string) {
		const pageName = getPageNameFromUrl(this.currentUrl);

		const evar = getPageLoadEvars(pageName);
		const prop = getCtaClickProps(InteractionContext.Navigating, 'Navigation', ctaTitle);

		this.adobeAnalyticsService.trackCtaClick(evar, prop);
	}

	setClientManagementModal(navLabel: string): void {
		if (navLabel === NAVIGATION_LABEL.ClientManagement) {
			this.realtorFacade.setOpenClientManagementModal(true);
		}
	}
}